import * as React from 'react';

import numeral from 'numeral';
import {
    ComposedChart,
    CartesianGrid,
    Legend,
    Tooltip,
    XAxis,
    YAxis,
    ResponsiveContainer,
    // @ts-expect-error
} from 'recharts';
import _ from 'underscore';

import type {ChartBucket} from 'nutshell-core/types';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';

import {SEGMENTS} from '../../report-constants';
import type {ReportFacet, ChartReportParams} from '../../report-types';

// @ts-expect-error
import {validTickLabelNodeProps} from '../index';
import {XAxisLabel} from '../x-axis-label';

import styles from './hero-chart.module.css';

const SHARED_Y_AXIS_PROPS = {
    axisLine: false,
    tickLine: false,
} as const;
const MATCH_LAST_SPACE = /\s(?=\S*$)/;

const DOMAIN_PADDING = {
    top: 120,
    right: 16,
    bottom: 0,
    left: 0,
} as const;
const SVG_WRAPPER_STYLES = {margin: '0 auto'} as const;

type RenderSeriesFunction = (data: {facet: ReportFacet}) => any;

type TimeseriesChartProps = {
    reportParams: ChartReportParams;
    chartBuckets: Array<ChartBucket>;
    currencyPrefix: string;
    isRefreshing?: boolean;
    renderTooltip?: (...args: any) => any;
    renderSeries: RenderSeriesFunction;
    onClickChartSegment?: (...args: any) => any;
    onMouseEnterLegendItem?: (arg1: {value: string}) => any;
    onMouseLeaveLegendItem?: (arg1: {value: string}) => any;
    chartHeader?: React.ReactElement<any>;
    legendAlignment: 'left' | 'right';
    xAxisLabel?: any;
    width?: number;
    height?: number;
};

export class TimeseriesChart extends React.PureComponent<TimeseriesChartProps> {
    render() {
        const isRefreshing = this.props.isRefreshing;

        return (
            <div
                className={
                    isRefreshing
                        ? styles['timeseries-container--loading']
                        : styles['timeseries-container']
                }
            >
                <Chart
                    {...this.props}
                    renderXAxisTickLabel={this.renderXAxisTickLabel}
                    renderYAxisTickLabel={this.renderYAxisTickLabel}
                    renderTooltip={!isRefreshing ? this.props.renderTooltip : undefined}
                    onClickChartSegment={
                        !isRefreshing && this.props.onClickChartSegment
                            ? this.handleClick
                            : undefined
                    }
                />
                {isRefreshing && <LoadingIcon className={styles['refreshing-icon']} />}
            </div>
        );
    }

    renderXAxisTickLabel = (props: any) => {
        // @ts-expect-error
        const lines = props.payload.value.split(MATCH_LAST_SPACE).map((line) => {
            return (
                <tspan key={line} textAnchor='middle' x={props.x} dy='1.1em'>
                    {line}
                </tspan>
            );
        });
        const validNodeProps = _.pick(props, validTickLabelNodeProps);

        return (
            <text {...validNodeProps} fill={colors.grey}>
                {lines}
            </text>
        );
    };

    renderYAxisTickLabel = (opts: {format?: string; prefix?: boolean; currencyPrefix: string}) => {
        const defaultFormat = '0[.]0a';
        const options = {format: defaultFormat, prefix: false, ...opts} as const;

        return (props: any) => {
            const value = props.payload.value;
            const numeralFormat = numeral(value).format(options.format);
            const validNodeProps = _.pick(props, validTickLabelNodeProps);

            let result = '';
            if (value !== 0) {
                result = options.prefix ? opts.currencyPrefix + numeralFormat : numeralFormat;
            }

            return (
                <text {...validNodeProps} fill={colors.grey} dy='3.5px'>
                    {result}
                </text>
            );
        };
    };

    handleClick = (data: {
        activePayload?: Array<{
            payload: {
                bucketData: {
                    filters: any;
                };
            };
        }>;
    }) => {
        // This sucks, has to be a better way
        if (
            this.props.onClickChartSegment &&
            data &&
            data.activePayload &&
            data.activePayload.length
        ) {
            // @ts-expect-error
            this.props.onClickChartSegment(data.activePayload[0].payload.bucketData.filters);
        }
    };
}

type ChartProps = {
    chartHeader?: React.ReactNode;
    chartBuckets: Array<ChartBucket>;
    reportParams: ChartReportParams;
    currencyPrefix: string;
    xAxisLabel?: React.ReactNode;
    renderXAxisTickLabel: (...args: any) => any;
    renderYAxisTickLabel: (...args: any) => any;
    renderTooltip?: (...args: any) => any;
    renderSeries: RenderSeriesFunction;
    onClickChartSegment?: (...args: any) => any;
    legendAlignment?: 'left' | 'right';
    onLegendItemClick?: (arg1: {value: string}) => any;
    onMouseEnterLegendItem?: (arg1: {value: string}) => any;
    onMouseLeaveLegendItem?: (arg1: {value: string}) => any;
    width?: number;
    height?: number;
};

function Chart(props: ChartProps) {
    const isTotal = props.reportParams.facet === 'total';
    const clickableStyles = typeof props.onClickChartSegment === 'function' ? styles.clickable : '';

    const xAxisInterval = 'preserveEnd';

    const legendWrapperStyle = {
        width: 'auto',
        top: props.legendAlignment === 'left' ? 24 : 64,
        right: props.legendAlignment === 'left' ? undefined : 16,
        left: props.legendAlignment === 'left' ? 16 : undefined,
        fontSize: 13,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
    } as const;

    let barCategoryGap;
    const segmentInfo = SEGMENTS.find((seg) => seg.id === props.reportParams.segment);
    if (segmentInfo && !segmentInfo.shouldStack[props.reportParams.facet]) {
        barCategoryGap = getCategoryGap(props.chartBuckets.length * 2);
    } else {
        barCategoryGap = getCategoryGap(props.chartBuckets.length);
    }

    const chartHeight = props.height || '100%';
    const composedChart = (
        <ResponsiveContainer height={chartHeight} width='100%'>
            <ComposedChart
                width={props.width}
                height={props.height}
                data={props.chartBuckets}
                margin={DOMAIN_PADDING}
                onClick={props.onClickChartSegment}
                barCategoryGap={barCategoryGap}
                style={SVG_WRAPPER_STYLES}
            >
                <CartesianGrid stroke={colors.offWhiteMd} vertical={false} />
                <XAxis
                    dataKey='name'
                    tick={props.renderXAxisTickLabel}
                    interval={xAxisInterval}
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    {...SHARED_Y_AXIS_PROPS}
                    allowDecimals={isTotal}
                    tickCount={isTotal ? 7 : undefined}
                    tick={props.renderYAxisTickLabel({
                        prefix: isTotal,
                        currencyPrefix: props.currencyPrefix,
                    })}
                />
                {props.renderTooltip && (
                    <Tooltip
                        content={props.renderTooltip}
                        cursor={{fill: colors.offWhiteMd, strokeWidth: 1, cursor: 'pointer'}}
                    />
                )}
                <Legend
                    iconSize={15}
                    wrapperStyle={legendWrapperStyle}
                    onClick={props.onLegendItemClick}
                    onMouseEnter={props.onMouseEnterLegendItem}
                    onMouseLeave={props.onMouseLeaveLegendItem}
                />
                {props.renderSeries({facet: props.reportParams.facet})}
            </ComposedChart>
        </ResponsiveContainer>
    );

    return (
        <div
            style={{height: chartHeight, width: '100%'}}
            className={`${clickableStyles} fade-in-animation`}
        >
            {props.chartHeader}
            {composedChart}
            {props.xAxisLabel ? <XAxisLabel>{props.xAxisLabel}</XAxisLabel> : undefined}
        </div>
    );
}

/*
Make bars more narrow as there are fewer of them.  Otherwise they end up being
super-wide and ugly.
 */
// @ts-expect-error
function getCategoryGap(barCount) {
    if (barCount >= 10) {
        return '15%';
    }

    if (barCount >= 5) {
        return '35%';
    }

    if (barCount >= 3) {
        return '40%';
    }

    return '45%';
}
